import { AssignmentType, LoadunitDto, ProjectLoadunitDto } from '../generated/backend';
import { AssignmentHelper } from '../util/assignment-helper';
import { MathHelper } from '../util/math-helper';

export class Cargo {
  weight: number = 0;
  volume: number = 0;
  type!: AssignmentType;
  assignmentNo: number = 0;

  constructor(weight: number, volume: number, type: AssignmentType) {
    this.weight = weight;
    this.volume = volume;
    this.type = type;
  }

  removeCargo(weight: number, volume: number) {
    this.weight -= weight;
    this.volume -= volume;
  }

  addCargo(weight: number, volume: number) {
    this.weight += weight;
    this.volume += volume;
  }
}

// cargoLoad is a class that holds the cargo that is loaded in a loadunit. It is a list of cargo object
export class CargoLoad extends Array<ProjectLoadunitDto> {
  constructor(items?: ProjectLoadunitDto[]) {
    super(...(items ? Array.from(items) : []));
  }

  public serialize(items: ProjectLoadunitDto[]): void {
    this.splice(0, this.length);
    this.addItems(items);
  }

  private addItems(items: ProjectLoadunitDto[]) {
    items.forEach(item => this.push(item));
  }

  public removeItem(item: ProjectLoadunitDto) {
    const index = this.indexOf(item);
    if (index > -1) {
      this.splice(index, 1);
    }
  }

  weightTotal = (assignmentType: AssignmentType) =>
    this.reduce(
      (acc, item) => acc + (item.Type == assignmentType ? (item.CurrentWeight ?? 0) + (item.CurrentWeight_B ?? 0) : 0),
      0
    ); // TODO: _B
  volumeTotal = (assignmentType: AssignmentType) =>
    this.reduce(
      (acc, item) => acc + (item.Type == assignmentType ? (item.CurrentVolume ?? 0) + (item.CurrentVolume_B ?? 0) : 0),
      0
    );

  inTotal = (): Cargo => {
    const inGoing = this.filter(
      item => AssignmentHelper.isDelivery(item.Type)
    );
    return new Cargo(
      inGoing.reduce((acc, item) => acc + (item.CurrentWeight ?? 0), 0),
      inGoing.reduce((acc, item) => acc + (item.CurrentVolume ?? 0), 0),
      AssignmentType.AssignmentDelivery
    );
  };

  // ADR points are a property of the assignment itself (hazmat classification), not of the
  // individual row - an assignment planned as multiple rows (e.g. a delivery row and a
  // pickup row sharing the same AssignmentNo) must only contribute its points once, not
  // once per row.
  static sumUniqueAdrPoints(items: Iterable<ProjectLoadunitDto>): number {
    const seenAssignmentNos = new Set<number>();
    let total = 0;
    for (const item of items) {
      if (item.AssignmentNo == null || seenAssignmentNos.has(item.AssignmentNo)) {
        continue;
      }
      seenAssignmentNos.add(item.AssignmentNo);
      total += item.AdrPoints ?? 0;
    }
    return total;
  }

  adrPointsTotal = (): number => CargoLoad.sumUniqueAdrPoints(this);

  isPrime = (): boolean => this.reduce((acc, item) => acc || (item.IsPrimeLogistic ?? false), false);

  outTotal = (): Cargo => {
    const outGoing = this.filter(
      item => AssignmentHelper.isPickup(item.Type)
    );
    return new Cargo(
      outGoing.reduce((acc, item) => acc + (item.CurrentWeight_B ?? 0), 0),
      outGoing.reduce((acc, item) => acc + (item.CurrentVolume_B ?? 0), 0),
      AssignmentType.AssignmentPickup
    );
  };
}

export class LoadUnitSlot {
  loadUnitContainer!: LoadunitDto; // the used container
  cargoLoad: CargoLoad = new CargoLoad();

  overload = () => {
    return (
      MathHelper.roundTo4(this.cargoLoad.inTotal().volume) > (MathHelper.roundTo4(this.loadUnitContainer.maxVolume) + 0.001) ||
      MathHelper.roundTo4(this.cargoLoad.outTotal().volume) > (MathHelper.roundTo4(this.loadUnitContainer.maxVolume) + 0.001)
    );
  };

  constructor(loadunitContainer: LoadunitDto) {
    this.loadUnitContainer = loadunitContainer;
  }

  get id () {
    return this.loadUnitContainer.id;
  }


  canFitAssignment(assignmentType: AssignmentType, weight: number, volume: number) {
    const weightFits = this.cargoLoad.weightTotal(assignmentType) + (weight ?? 0) < this.loadUnitContainer.maxLoad;
    const volumeFits = this.cargoLoad.volumeTotal(assignmentType) + (volume ?? 0) < this.loadUnitContainer.maxVolume;

    // console.log('canFitAssignment', weight, volume);
    return weightFits && volumeFits;
  }
}
